/** Shared game types for Rogue's Cog */

import { type HexCoord, type Vec2, coordToKey } from './grid/hex';
import type { PRNG } from './rng';
import type { ScoreBreakdown } from './scoring/score.js';

export type { HexCoord, Vec2 };

export enum BubbleColor {
  Red = 'Red',
  Blue = 'Blue',
  Green = 'Green',
  Yellow = 'Yellow',
  Purple = 'Purple',
  Orange = 'Orange',
}

/** All bubble colors in unlock order */
export const ALL_BUBBLE_COLORS: readonly BubbleColor[] = [
  BubbleColor.Red,
  BubbleColor.Blue,
  BubbleColor.Green,
  BubbleColor.Yellow,
  BubbleColor.Purple,
  BubbleColor.Orange,
];

export type GameMode = 'endless' | 'daily' | 'pvp' | 'practice';
export type GameStatus = 'aiming' | 'drafting' | 'game-over';

/**
 * Why a game ended. Only meaningful when status === 'game-over'.
 * - 'overflow': the ceiling descended into the shooter — defeat.
 * - 'shots-exhausted': the player fired every shot in their budget — victory
 *    (currently only the Daily Challenge uses a shot budget).
 */
export type GameEndReason = 'overflow' | 'shots-exhausted';

/**
 * Runtime tuple of every GameMode. Used by replay validation
 * (src/game/replay/format.ts) so a parser can reject unknown modes without
 * each call site maintaining its own list.
 *
 * The `satisfies readonly GameMode[]` + the `_ExhaustiveGameModes` never-check
 * below together form a compile-time assertion: adding a value to the
 * GameMode union without also adding it here fails `pnpm typecheck`.
 */
export const ALL_GAME_MODES = [
  'endless',
  'daily',
  'pvp',
  'practice',
] as const satisfies readonly GameMode[];

export const ALL_GAME_STATUSES = [
  'aiming',
  'drafting',
  'game-over',
] as const satisfies readonly GameStatus[];

type _ExhaustiveGameModes =
  Exclude<GameMode, typeof ALL_GAME_MODES[number]> extends never ? true : false;
type _ExhaustiveGameStatuses =
  Exclude<GameStatus, typeof ALL_GAME_STATUSES[number]> extends never ? true : false;

// If either assignment fails, the union has a value missing from the tuple.
const _exhaustiveGameModes: _ExhaustiveGameModes = true;
const _exhaustiveGameStatuses: _ExhaustiveGameStatuses = true;
void _exhaustiveGameModes;
void _exhaustiveGameStatuses;

export interface MutationCard {
  readonly type: string;
  readonly name: string;
  readonly description: string;
  readonly flavorText: string;
  readonly sentiment: 'harmful' | 'beneficial' | 'mixed';
  /**
   * Enhancement level (Mk. I..V) this card grants when picked. Set on
   * beneficial draft options only; harmful cards have no ladder.
   */
  readonly level?: number;
}

export interface MutationDraft {
  /** The harmful card auto-applied when the draft triggers. */
  readonly harmful: MutationCard;
  /**
   * Up to three beneficial cards the player picks from. Fewer when most
   * blueprints are already at Mk. V; the engine never enters `drafting`
   * with zero options.
   */
  readonly options: MutationCard[];
}

export interface ActiveMutation {
  readonly type: string;
  readonly turnsRemaining: number;
  readonly shotsRemaining?: number;
  /** Enhancement level for one-shot buffs whose magnitude is resolved at fire time (Powder Keg, Cascade Coil). */
  readonly level?: number;
  readonly payload?: { colors?: BubbleColor[] };
}

/** Picks-per-blueprint for the current run (see mutations/levels.ts). */
export type EnhancementLevels = Readonly<Record<string, number>>;

export interface GameState {
  readonly board: BoardState;
  readonly projectile: null;
  readonly nextColors: BubbleColor[];
  readonly heldBubble: BubbleColor | null;
  readonly score: number;
  readonly multiplierX10: number;
  readonly turnNumber: number;
  readonly seed: string;
  readonly rng: PRNG;
  readonly pendingDraft: MutationDraft | null;
  readonly activeMutations: ActiveMutation[];
  /** How many times each beneficial blueprint has been drafted this run. */
  readonly enhancementLevels: EnhancementLevels;
  readonly turnsUntilDraft: number;
  readonly status: GameStatus;
  readonly mode: GameMode;
  readonly isTutorial: boolean;
  readonly tutorialStep: number;
  readonly nearFailureCount: number;
  readonly lastScoreBreakdown: ScoreBreakdown[] | null;
  readonly gamesPlayed: number;
  readonly shotClockMs: number | null;
  readonly shotClockStartedAt: number | null;
  /** Number of drafts completed so far (used to scale draft interval). */
  readonly draftCount: number;
  /** Timestamp (ms) when this game started. */
  readonly gameStartedAt: number;
  /**
   * Milliseconds the ceiling has actually been descending this run — drives
   * the descent speed ramp. Frozen while the descent is halted (pause,
   * drafting, Slow the Clockwork, Cascade Coil hold) so idle time on the
   * pause screen doesn't make the tower faster.
   */
  readonly descentElapsedMs: number;
  /** Timestamp (ms) of the last ceiling descent row injection. */
  readonly lastDescentAt: number;
  /** Fractional descent offset in pixels — drives smooth continuous descent. */
  readonly descentOffset: number;
  /** Timestamp (ms) of the last Rogue theft, or game start. Drives the 45s theft cooldown. */
  readonly lastTheftAt: number;
  /** Why the run ended. Null while the run is live. */
  readonly endReason: GameEndReason | null;
}

export interface Bubble {
  readonly coord: HexCoord;
  readonly color: BubbleColor;
  readonly isWild: boolean;
  readonly isBomb: boolean;
  readonly isObstacle: boolean;
}

export interface BoardState {
  readonly bubbles: Map<string, Bubble>;
  readonly width: number;
  readonly ceilingRow: number;
  readonly activeColors: BubbleColor[];
}

/** Get a bubble from the board by coordinate. */
export function getBubble(board: BoardState, coord: HexCoord): Bubble | undefined {
  return board.bubbles.get(coordToKey(coord));
}

/** Return a new board with the bubble added at the given coordinate. */
export function addBubble(
  board: BoardState,
  coord: HexCoord,
  color: BubbleColor,
  options: { isWild?: boolean; isBomb?: boolean; isObstacle?: boolean } = {},
): BoardState {
  const bubble: Bubble = {
    coord,
    color,
    isWild: options.isWild ?? false,
    isBomb: options.isBomb ?? false,
    isObstacle: options.isObstacle ?? false,
  };
  const newBubbles = new Map(board.bubbles);
  newBubbles.set(coordToKey(coord), bubble);
  return { ...board, bubbles: newBubbles };
}

/** Return a new board with the specified bubbles removed. */
export function removeBubbles(board: BoardState, coords: ReadonlyArray<HexCoord>): BoardState {
  const newBubbles = new Map(board.bubbles);
  for (const coord of coords) {
    newBubbles.delete(coordToKey(coord));
  }
  return { ...board, bubbles: newBubbles };
}
